<template>
    <div class="checkout-addons-header">
        <p class="checkout-addons-title">
            {{ t({ key: 'shared.addons' }) }}
        </p>
    </div>

    <Box padding="none" :radius="isTablet ? 's' : 'none'" :overflowHidden="true">
        <div class="checkout-addons">
            <FormV2
                id="form_addons"
                :key="formKey"
                :onValidChange="handleValidChange"
                :validationSchema="validationSchema"
                :initialValues="initialValues">
                <Field
                    v-for="(addon, index) in addons"
                    :key="index"
                    :name="addon.$type"
                    v-slot="{ field, errors }">
                    <AddonItem
                        v-model="field.value"
                        :onUpdate:modelValue="
                            (value) => {
                                if (field['onUpdate:modelValue']) {
                                    field['onUpdate:modelValue'](value);
                                    handleTouchedField(value, addon.$type);
                                }
                            }
                        "
                        :item="addon"
                        :errors="errors" />
                </Field>
            </FormV2>

            <Transfer />
        </div>
    </Box>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { defineComponent, computed, ref, watch } from 'vue';
    import { useStore } from '@/store';
    import { AnyProduct } from '@shared/interfaces/product';
    import { AddonProduct } from '@/api/Cart/cart.interfaces';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import { ValidationSchema } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import { isDefined } from '@ui/components/FormV2/Validations/functions';
    import { Field } from 'vee-validate';
    import { useScreenSize } from '@shared/helpers/viewport';
    import { generateUUID } from '@shared/utils/uuid';
    import { CheckoutStepState, CheckoutStepStateField } from '@/store/checkout/types';
    import Box from '@ui/components/Box/Box.vue';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import AddonItem from './AddonItem/AddonItem.vue';
    import Transfer from './Transfer/Transfer.vue';

    export default defineComponent({
        name: 'Addons',
        methods: { t },
        props: { sectionId: String },
        components: { FormV2, Field, Box, AddonItem, Transfer },
        setup(props) {
            const store = useStore();
            const { isTablet } = useScreenSize();

            const formKey = ref<string | undefined>(undefined);

            const products = computed((): AnyProduct[] => store.getters['cart/getProducts']);

            const addons = computed(
                (): AddonProduct[] => store.getters['cart/getAvailableAddons']
            );

            const fields = computed<CheckoutStepStateField[]>(() => {
                const states: CheckoutStepState[] = store.getters['checkout/getStates'];

                return states.find((x) => x.id === props.sectionId)?.fields ?? [];
            });

            const validationSchema = computed((): ValidationSchema => {
                const schema: ValidationSchema = {};

                addons.value.forEach((addon) => {
                    schema[addon.$type] = [isDefined];
                });

                return schema;
            });

            const initialValues = computed(() => {
                const values: FormValues = {};

                addons.value.forEach((addon) => {
                    const field = fields.value.find((x) => x.name === addon.$type);

                    const match = products.value.find((x) => x.$type === addon.$type);

                    const defaultValue = field && field.touched ? field.value : undefined;

                    values[addon.$type] = match ? true : defaultValue;
                });

                return values;
            });

            function handleValidChange(valid: boolean) {
                store.dispatch('checkout/setValid', {
                    id: props.sectionId,
                    valid,
                });
            }

            function handleTouchedField(value: boolean | undefined, name: string) {
                store.dispatch('checkout/setTouchedField', {
                    id: props.sectionId,
                    name,
                    value,
                });
            }

            watch(
                addons,
                (newAddons, oldAddons) => {
                    const addonsChanged = newAddons.length !== oldAddons?.length;

                    if (addonsChanged) {
                        formKey.value = generateUUID();
                    }
                },
                { immediate: true }
            );

            return {
                formKey,
                addons,
                isTablet,
                initialValues,
                validationSchema,
                handleValidChange,
                handleTouchedField,
            };
        },
    });
</script>

<style lang="scss">
    @forward 'Addons.scss';
</style>
